import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { AppLinkConfig, AppLinkValidationResult } from './types';

/**
 * @param config - App link configuration defining required/optional params
 * @returns Validation result with extracted params and cleanUrl function
 */
export const useAppLinkParams = (
  config: AppLinkConfig,
): AppLinkValidationResult & {
  cleanUrl: () => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const validation = useMemo<AppLinkValidationResult>(() => {
    const params: Record<string, string> = {};
    const missingParams: string[] = [];
    config.requiredParams.forEach((paramName) => {
      const value = searchParams.get(paramName);
      if (value === null || value === '') {
        missingParams.push(paramName);
      } else {
        params[paramName] = value;
      }
    });
    if (config.optionalParams) {
      config.optionalParams.forEach((paramName) => {
        const value = searchParams.get(paramName);
        if (value !== null && value !== '') {
          params[paramName] = value;
        }
      });
    }
    const isValid = missingParams.length === 0;
    const error = isValid ? null : `Missing required parameters: ${missingParams.join(', ')}`;

    return {
      isValid,
      params,
      missingParams,
      error,
    };
  }, [searchParams, config.requiredParams, config.optionalParams]);

  const cleanUrl = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    config.requiredParams.forEach((paramName) => {
      newSearchParams.delete(paramName);
    });
    if (config.optionalParams) {
      config.optionalParams.forEach((paramName) => {
        newSearchParams.delete(paramName);
      });
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams, config.requiredParams, config.optionalParams]);

  return {
    ...validation,
    cleanUrl,
  };
};
